(() => {
  const componentName = 'order-tracking';
  const defaultStrings = Object.freeze({
    submit: 'TRACK ORDER',
    loading: 'CHECKING ORDER...',
    invalidOrder: 'Enter an order number using digits only (e.g. 1001).',
    invalidEmail: 'Enter a valid email address.',
    notFoundTitle: 'We could not find that order',
    notFound:
      'Please check that your order number and email address match your order confirmation. Orders placed in the last few minutes may take a little while to appear.',
    noShipmentTitle: 'Your order is being prepared',
    noShipment:
      'Good news — we found your order! It has not shipped yet, so there is no tracking information available. Your items are still being produced and packed at our workshop. As soon as the carrier collects your parcel, the tracking number will appear here and we will email it to you.',
    rateLimited: 'There have been too many requests. Wait a moment, then try again.',
    unavailable: 'We cannot retrieve tracking details right now. Please try again in a few minutes.',
    timeout: 'The request took too long. Check your connection and try again.',
    dismiss: 'Close',
    dismissPending: 'Got it',
    dismissNotFound: 'Check my details',
    backToSearch: 'Back to search',
    contactSupport: 'Contact support',
    order: 'Order ID',
    carrier: 'Carrier',
    carrierUnknown: 'Carrier unavailable',
    trackingId: 'Tracking ID',
    copy: 'Copy',
    copied: 'Copied',
    updated: 'Updated',
    activity: 'Shipment activity',
    activityUpdate: 'Tracking update',
    latest: 'Latest',
    noActivity:
      'The carrier has accepted your parcel but has not published any scans yet. Tracking events usually appear within 24-48 hours.',
    viewCarrier: 'View carrier site',
  });

  if (customElements.get(componentName)) return;

  class OrderTracking extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.requestId = 0;
      this.copyTimers = new Set();
      this.config = this.readConfig();
      this.formCard = this.querySelector('.tg-order-tracking__form-card');
      this.form = this.querySelector('[data-tracking-form]');
      this.orderInput = this.querySelector('[name="orderNumber"]');
      this.emailInput = this.querySelector('[name="email"]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.submitSpinner = this.querySelector('[data-submit-spinner]');
      this.formStatus = this.querySelector('[data-form-status]');
      this.results = this.querySelector('[data-tracking-results]');
      this.dialog = this.querySelector('[data-tracking-dialog]');
      this.dialogInner = this.querySelector('[data-dialog-inner]');
      this.dialogBody = this.querySelector('[data-dialog-body]');
      this.dialogCloseButton = this.querySelector('[data-dialog-close]');

      if (!this.form || !this.config) return;

      this.onSubmit = this.handleSubmit.bind(this);
      this.onFieldInput = this.handleFieldInput.bind(this);
      this.onDialogClick = this.handleDialogClick.bind(this);
      this.onDialogClose = this.handleDialogClose.bind(this);
      this.onDialogCloseClick = () => this.closeDialog();
      this.form.addEventListener('submit', this.onSubmit);
      this.orderInput?.addEventListener('input', this.onFieldInput);
      this.emailInput?.addEventListener('input', this.onFieldInput);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.dialogCloseButton?.addEventListener('click', this.onDialogCloseClick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.orderInput?.removeEventListener('input', this.onFieldInput);
      this.emailInput?.removeEventListener('input', this.onFieldInput);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.dialogCloseButton?.removeEventListener('click', this.onDialogCloseClick);
      this.copyTimers?.forEach((timer) => window.clearTimeout(timer));
      this.copyTimers?.clear();
    }

    handleDialogClick(event) {
      // A click that lands on the dialog itself is a click on the backdrop.
      if (event.target === this.dialog) this.closeDialog();
    }

    handleDialogClose() {
      this.dialogBody?.replaceChildren();

      const restoreFocus = this.dialogReturnFocus;
      this.dialogReturnFocus = null;
      if (this.isConnected) restoreFocus?.();
    }

    openDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.hasAttribute('open')) this.dialog.showModal();
      } else {
        // Fallback for browsers without <dialog>: the CSS renders [open] as a fixed overlay.
        this.dialog.setAttribute('open', '');
        this.dialogCloseButton?.focus();
      }
    }

    closeDialog() {
      if (!this.dialog?.hasAttribute('open')) return;

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    readConfig() {
      const configElement = this.querySelector('[data-tracking-config]');

      if (!configElement) return null;

      try {
        const config = JSON.parse(configElement.textContent);
        return {
          ...config,
          strings: {
            ...defaultStrings,
            submit: config.submitLabel || defaultStrings.submit,
            ...this.readMessageOverrides(config.messages),
          },
        };
      } catch (error) {
        console.error('Order tracking configuration is invalid.', error);
        return null;
      }
    }

    readMessageOverrides(messages) {
      if (!messages || typeof messages !== 'object') return {};

      // Merchants can blank out a field in the theme editor to fall back to the built-in copy.
      return Object.entries(messages).reduce((overrides, [key, value]) => {
        const text = typeof value === 'string' ? value.trim() : '';
        if (text && key in defaultStrings) overrides[key] = text;
        return overrides;
      }, {});
    }

    // Clear corrected errors while typing so blur cannot move the submit button during a click.
    handleFieldInput(event) {
      const field = event.currentTarget;

      if (field === this.orderInput && /^\d+$/.test(this.normalizeOrderNumber(field.value))) {
        this.clearFieldError('orderNumber');
      }

      if (field === this.emailInput && this.isValidEmail(field.value.trim())) {
        this.clearFieldError('email');
      }
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.config.endpoint) return;

      const values = this.validateForm();
      if (!values) return;

      const requestId = ++this.requestId;
      this.controller?.abort();
      this.controller = new AbortController();
      this.clearError();
      this.clearResults();
      this.setLoading(true);

      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        this.controller?.abort();
      }, 15000);

      try {
        const endpoint = new URL(this.config.endpoint, window.location.origin);
        endpoint.searchParams.set('orderNumber', values.orderNumber);
        endpoint.searchParams.set('email', values.email);
        endpoint.searchParams.set('shopName', this.config.shopName);

        const response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
          },
          signal: this.controller.signal,
        });

        const payload = await this.readResponse(response);

        if (requestId !== this.requestId) return;

        if (!response.ok || payload?.success !== true || !payload.data) {
          throw new OrderTrackingError(this.getResponseErrorCode(response, payload));
        }

        const trackingData = this.normalizeTrackingData(payload.data);
        if (trackingData.shipments.length === 0) {
          throw new OrderTrackingError('NO_SHIPMENT');
        }

        this.renderResults(trackingData, values.orderNumber);
      } catch (error) {
        if (requestId !== this.requestId || !this.isConnected) return;

        const errorCode = timedOut
          ? 'TIMEOUT'
          : error instanceof OrderTrackingError
            ? error.code
            : error?.name === 'AbortError'
              ? 'UPSTREAM_UNAVAILABLE'
              : 'UPSTREAM_UNAVAILABLE';

        this.renderError(errorCode);
      } finally {
        window.clearTimeout(timeout);

        if (requestId === this.requestId && this.isConnected) {
          this.setLoading(false);
        }
      }
    }

    async readResponse(response) {
      const responseText = await response.text();

      if (!responseText) return null;

      try {
        return JSON.parse(responseText);
      } catch (_error) {
        throw new OrderTrackingError('UPSTREAM_UNAVAILABLE');
      }
    }

    getResponseErrorCode(response, payload) {
      const suppliedCode = payload?.error?.code;

      if (typeof suppliedCode === 'string' && suppliedCode.trim()) {
        const code = suppliedCode.trim().toUpperCase();
        const aliases = {
          INVALID_REQUEST: 'INVALID_INPUT',
          ORDER_NOT_FOUND: 'NOT_FOUND',
          INTERNAL_ERROR: 'UPSTREAM_UNAVAILABLE',
          UPSTREAM_ERROR: 'UPSTREAM_UNAVAILABLE',
        };

        return aliases[code] || code;
      }

      if (response.status === 404) return 'NOT_FOUND';
      if (response.status === 429) return 'RATE_LIMITED';
      if (response.status === 400 || response.status === 422) return 'INVALID_INPUT';

      return 'UPSTREAM_UNAVAILABLE';
    }

    normalizeTrackingData(data) {
      if (Array.isArray(data.shipments)) return data;

      const trackingNumber = this.textValue(data.trackingNumber);
      if (!trackingNumber || data.queryStatus === 'no_tracking_number') {
        return { ...data, shipments: [] };
      }

      const details = Array.isArray(data.details) ? data.details : [];
      return {
        ...data,
        shipments: [
          {
            trackingNumber,
            carrier: data.carrier,
            status: {
              code: data.statusCode,
              label: data.statusLabel,
            },
            updatedAt: data.lastUpdateTime,
            events: details.map((detail) => ({
              occurredAt: detail?.time,
              description: detail?.description,
              location: detail?.location,
              statusCode: detail?.statusCode,
              status: detail?.statusLabel,
            })),
          },
        ],
      };
    }

    normalizeOrderNumber(value) {
      // Trim pasted punctuation at the edges without joining separate numbers or removing letters.
      return value.trim().replace(/^[\p{P}\p{S}\s]+|[\p{P}\p{S}\s]+$/gu, '');
    }

    validateForm() {
      this.clearFieldError('orderNumber');
      this.clearFieldError('email');

      const orderNumber = this.normalizeOrderNumber(this.orderInput?.value || '');
      const email = this.emailInput?.value.trim().toLowerCase() || '';
      let firstInvalidField = null;

      if (!/^\d+$/.test(orderNumber)) {
        this.setFieldError('orderNumber', this.config.strings.invalidOrder);
        firstInvalidField = this.orderInput;
      }

      if (!this.isValidEmail(email)) {
        this.setFieldError('email', this.config.strings.invalidEmail);
        firstInvalidField ||= this.emailInput;
      }

      if (firstInvalidField) {
        firstInvalidField.focus();
        return null;
      }

      this.orderInput.value = orderNumber;
      this.emailInput.value = email;

      return { orderNumber, email };
    }

    isValidEmail(value) {
      if (!value || !this.emailInput) return false;

      const previousValue = this.emailInput.value;
      this.emailInput.value = value;
      const valid = this.emailInput.validity.valid;
      this.emailInput.value = previousValue;

      return valid;
    }

    setFieldError(fieldName, message) {
      const input = fieldName === 'email' ? this.emailInput : this.orderInput;
      const error = this.querySelector(`[data-field-error="${fieldName}"]`);

      if (!input || !error) return;

      input.setAttribute('aria-invalid', 'true');
      error.textContent = message;
      error.hidden = false;
    }

    clearFieldError(fieldName) {
      const input = fieldName === 'email' ? this.emailInput : this.orderInput;
      const error = this.querySelector(`[data-field-error="${fieldName}"]`);

      input?.removeAttribute('aria-invalid');

      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton || !this.submitLabel || !this.submitSpinner) return;

      this.submitButton.disabled = isLoading || !this.config.endpoint;
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.submitLabel.textContent = isLoading ? this.config.strings.loading : this.config.strings.submit;
      this.submitSpinner.hidden = !isLoading;
      this.formStatus.textContent = isLoading ? this.config.strings.loading : '';
    }

    renderError(code) {
      if (!this.dialog || !this.dialogBody) return;

      // NO_SHIPMENT means the order exists but has not left the warehouse yet: reassure, do not alarm.
      const isPending = code === 'NO_SHIPMENT';
      const editableInput = code === 'NOT_FOUND' || code === 'INVALID_INPUT';
      const title = this.getErrorTitle(code);
      const message = this.getErrorMessage(code);

      const icon = this.createElement('span', 'tg-order-tracking__dialog-icon');
      icon.setAttribute('aria-hidden', 'true');
      icon.appendChild(this.cloneIcon('info'));

      const content = this.createElement('div', 'tg-order-tracking__dialog-content');

      if (title) {
        content.appendChild(this.createElement('h2', 'tg-order-tracking__dialog-title', title));
      }

      content.appendChild(this.createElement('p', 'tg-order-tracking__dialog-text', message));

      const actions = this.createElement('div', 'tg-order-tracking__dialog-actions');
      const dismiss = this.createElement(
        'button',
        'tg-order-tracking__dialog-action tg-order-tracking__dialog-action--primary',
        this.getDismissLabel(code),
      );
      dismiss.type = 'button';
      dismiss.addEventListener('click', () => this.closeDialog());
      actions.appendChild(dismiss);

      const contactUrl = this.safeUrl(this.config.contactUrl, true);
      if (contactUrl) {
        const contact = this.createElement(
          'a',
          'tg-order-tracking__dialog-action',
          this.config.strings.contactSupport,
        );
        contact.href = contactUrl;
        actions.appendChild(contact);
      }

      content.appendChild(actions);

      this.dialogInner.className = `tg-order-tracking__dialog-inner tg-order-tracking__dialog-inner--${
        isPending ? 'pending' : 'error'
      }`;
      this.dialog.setAttribute('aria-label', title || message);
      this.dialog.setAttribute('role', isPending ? 'dialog' : 'alertdialog');
      this.dialogBody.replaceChildren(icon, content);

      // Send focus back where the customer can act once the dialog closes.
      this.dialogReturnFocus = editableInput
        ? () => {
            this.orderInput?.focus();
            this.orderInput?.select();
          }
        : () => this.submitButton?.focus();

      this.openDialog();
    }

    getDismissLabel(code) {
      if (code === 'NO_SHIPMENT') return this.config.strings.dismissPending;
      if (code === 'NOT_FOUND' || code === 'INVALID_INPUT') return this.config.strings.dismissNotFound;

      return this.config.strings.dismiss;
    }

    getErrorTitle(code) {
      const titles = {
        NOT_FOUND: this.config.strings.notFoundTitle,
        NO_SHIPMENT: this.config.strings.noShipmentTitle,
      };

      return titles[code] || '';
    }

    getErrorMessage(code) {
      const messages = {
        INVALID_INPUT: this.config.strings.invalidOrder,
        NOT_FOUND: this.config.strings.notFound,
        NO_SHIPMENT: this.config.strings.noShipment,
        RATE_LIMITED: this.config.strings.rateLimited,
        TIMEOUT: this.config.strings.timeout,
        UPSTREAM_UNAVAILABLE: this.config.strings.unavailable,
      };

      return messages[code] || this.config.strings.unavailable;
    }

    clearError() {
      this.dialogReturnFocus = null;
      this.closeDialog();
      this.dialogBody?.replaceChildren();
    }

    clearResults() {
      if (!this.results) return;

      this.results.hidden = true;
      this.results.replaceChildren();
    }

    showSearchForm() {
      this.clearResults();
      this.clearError();
      this.formStatus.textContent = '';

      if (this.formCard) {
        this.formCard.hidden = false;
      }

      this.orderInput?.focus();
    }

    renderResults(data, submittedOrderNumber) {
      if (!this.results) return;

      const shipments = data.shipments.filter((shipment) => shipment && typeof shipment === 'object');
      if (shipments.length === 0) throw new OrderTrackingError('NO_SHIPMENT');

      const orderNumber = this.textValue(data.orderNumber) || submittedOrderNumber;
      const shipment = shipments[0];
      const card = this.createElement('article', 'tg-order-tracking__card tg-order-tracking__result-card');
      const details = this.createElement('div', 'tg-order-tracking__result-details');

      card.append(this.createResultSidebar(orderNumber, shipment), details);
      details.appendChild(this.createActivity(shipment.events));

      this.results.replaceChildren(card);
      if (this.formCard) {
        this.formCard.hidden = true;
      }
      this.results.hidden = false;
      this.formStatus.textContent = '';
      this.results.focus();
    }

    createResultSidebar(orderNumber, shipment) {
      const sidebar = this.createElement('aside', 'tg-order-tracking__result-sidebar');
      const backButton = this.createElement('button', 'tg-order-tracking__back');
      const backIcon = this.createElement('span', 'tg-order-tracking__back-icon');
      backButton.type = 'button';
      backIcon.setAttribute('aria-hidden', 'true');
      backIcon.appendChild(this.cloneIcon('arrow'));
      backButton.append(backIcon, this.createElement('span', '', this.config.strings.backToSearch));
      backButton.addEventListener('click', () => this.showSearchForm());

      const carrier = this.getCarrierName(shipment);
      const trackingNumber = this.textValue(shipment.trackingNumber);
      const metadata = this.createElement('div', 'tg-order-tracking__result-meta');
      const orderField = this.createElement('div', 'tg-order-tracking__summary-field');
      const carrierField = this.createElement('div', 'tg-order-tracking__summary-field');

      orderField.append(
        this.createElement('p', 'tg-order-tracking__eyebrow', this.config.strings.order),
        this.createElement('h2', 'tg-order-tracking__order-number', orderNumber),
      );
      carrierField.append(
        this.createElement('p', 'tg-order-tracking__eyebrow', this.config.strings.carrier),
        this.createElement('p', 'tg-order-tracking__carrier', carrier),
      );
      metadata.append(orderField, carrierField);

      if (trackingNumber) {
        metadata.appendChild(this.createTrackingRow(trackingNumber));
      }

      const updatedAt = this.textValue(shipment.updatedAt) || this.getLatestEventDate(shipment.events);
      if (updatedAt) {
        metadata.appendChild(
          this.createElement(
            'p',
            'tg-order-tracking__updated',
            `${this.config.strings.updated}: ${this.formatDate(updatedAt)}`,
          ),
        );
      }

      sidebar.append(backButton, metadata);

      const trackingUrl = this.safeUrl(shipment.trackingUrl);
      if (trackingUrl) {
        const carrierLink = this.createElement(
          'a',
          'tg-order-tracking__carrier-link',
          this.config.strings.viewCarrier,
        );
        carrierLink.href = trackingUrl;
        carrierLink.target = '_blank';
        carrierLink.rel = 'noopener noreferrer';
        carrierLink.appendChild(this.cloneIcon('arrow'));
        sidebar.appendChild(carrierLink);
      }

      return sidebar;
    }

    createTrackingRow(trackingNumber) {
      const row = this.createElement('div', 'tg-order-tracking__tracking-row');
      const value = this.createElement('div', 'tg-order-tracking__tracking-value');
      row.appendChild(this.createElement('span', 'tg-order-tracking__meta-label', this.config.strings.trackingId));

      const copyButton = this.createElement('button', 'tg-order-tracking__copy');
      copyButton.type = 'button';
      copyButton.setAttribute('aria-label', `${this.config.strings.copy} ${trackingNumber}`);
      copyButton.title = this.config.strings.copy;
      copyButton.appendChild(this.cloneIcon('copy'));
      copyButton.addEventListener('click', async () => {
        const copied = await this.copyText(trackingNumber);
        if (!copied) return;

        copyButton.replaceChildren(this.cloneIcon('check'));
        copyButton.setAttribute('aria-label', `${this.config.strings.copied}: ${trackingNumber}`);
        copyButton.title = this.config.strings.copied;

        const timer = window.setTimeout(() => {
          copyButton.replaceChildren(this.cloneIcon('copy'));
          copyButton.setAttribute('aria-label', `${this.config.strings.copy} ${trackingNumber}`);
          copyButton.title = this.config.strings.copy;
          this.copyTimers.delete(timer);
        }, 3000);
        this.copyTimers.add(timer);
      });

      value.append(this.createElement('span', 'tg-order-tracking__tracking-number', trackingNumber), copyButton);
      row.appendChild(value);
      return row;
    }

    createActivity(events) {
      const section = this.createElement('section', 'tg-order-tracking__activity');
      section.appendChild(this.createElement('h3', 'tg-order-tracking__section-heading', this.config.strings.activity));
      const sortedEvents = this.sortEvents(events);

      if (sortedEvents.length === 0) {
        section.appendChild(
          this.createElement('p', 'tg-order-tracking__empty-activity', this.config.strings.noActivity),
        );
        return section;
      }

      const list = this.createElement('ol', 'tg-order-tracking__activity-list');
      sortedEvents.forEach((event, index) => {
        const item = this.createElement('li', 'tg-order-tracking__event');
        const marker = this.createElement('span', 'tg-order-tracking__event-marker');
        marker.setAttribute('aria-hidden', 'true');
        const content = this.createElement('div', 'tg-order-tracking__event-content');
        const topLine = this.createElement('div', 'tg-order-tracking__event-topline');
        const description =
          this.textValue(event.description) || this.textValue(event.status) || this.config.strings.activityUpdate;
        topLine.appendChild(this.createElement('p', 'tg-order-tracking__event-description', description));

        if (index === 0) {
          topLine.appendChild(this.createElement('span', 'tg-order-tracking__latest', this.config.strings.latest));
        }

        content.appendChild(topLine);
        const location = this.textValue(event.location);
        const occurredAt = this.textValue(event.occurredAt);

        if (location) {
          content.appendChild(this.createElement('p', 'tg-order-tracking__event-location', location));
        }

        if (occurredAt) {
          const time = this.createElement('time', 'tg-order-tracking__event-time', this.formatDate(occurredAt));
          time.dateTime = occurredAt;
          content.appendChild(time);
        }

        item.append(marker, content);
        list.appendChild(item);
      });
      section.appendChild(list);

      return section;
    }

    sortEvents(events) {
      if (!Array.isArray(events)) return [];

      return events
        .filter((event) => event && typeof event === 'object')
        .map((event, index) => ({ event, index, time: Date.parse(event.occurredAt) }))
        .sort((a, b) => {
          if (Number.isNaN(a.time) && Number.isNaN(b.time)) return a.index - b.index;
          if (Number.isNaN(a.time)) return 1;
          if (Number.isNaN(b.time)) return -1;
          return b.time - a.time;
        })
        .map(({ event }) => event);
    }

    getLatestEventDate(events) {
      return this.textValue(this.sortEvents(events)[0]?.occurredAt);
    }

    getCarrierName(shipment) {
      if (typeof shipment.carrier === 'string') return shipment.carrier;

      return (
        this.textValue(shipment.carrier?.name) ||
        this.textValue(shipment.carrierName) ||
        this.config.strings.carrierUnknown
      );
    }

    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      try {
        return new Intl.DateTimeFormat(this.config.locale || undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(date);
      } catch (_error) {
        return date.toLocaleString();
      }
    }

    async copyText(value) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
          return true;
        }

        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        return copied;
      } catch (_error) {
        return false;
      }
    }

    safeUrl(value, allowRelative = false) {
      const stringValue = this.textValue(value);
      if (!stringValue) return '';

      try {
        const url = new URL(stringValue, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        if (!allowRelative && url.origin === window.location.origin && !stringValue.startsWith('http')) return url.href;
        return url.href;
      } catch (_error) {
        return '';
      }
    }

    textValue(value) {
      return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    }

    createElement(tagName, className = '', text = '') {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      if (text !== '') element.textContent = text;
      return element;
    }

    cloneIcon(name) {
      const template = this.querySelector(`[data-icon-template="${name}"]`);
      return template ? template.content.cloneNode(true) : document.createDocumentFragment();
    }

  }

  class OrderTrackingError extends Error {
    constructor(code) {
      super(code);
      this.name = 'OrderTrackingError';
      this.code = code;
    }
  }

  customElements.define(componentName, OrderTracking);
})();
